import { Injectable } from '@angular/core';
import {
  DEFAULT_ZRA_TAX_CODES,
  ZraTaxCode,
  ZraTaxCodesService,
} from '@boma/supplier-portal/shared/services';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
  take,
  takeUntil,
  Subject,
} from 'rxjs';
import { getSelectedSupplierId } from '../shared-state';

export interface TaxCodesState {
  taxCodes: ZraTaxCode[];
  loading: boolean;
  error: string | null;
}

@Injectable()
export class TaxCodesFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<TaxCodesState> =
    new BehaviorSubject<TaxCodesState>({
      taxCodes: [],
      loading: false,
      error: null,
    });
  private readonly _loadCancellation$ = new Subject<void>();

  loading$ = this._state$.pipe(map((state) => state.loading));
  error$ = this._state$.pipe(map((state) => state.error));
  taxCodes$ = this._state$.pipe(map((state) => state.taxCodes));

  constructor(
    private store: Store,
    private taxCodesService: ZraTaxCodesService
  ) {
    this._entityId$ = this.store
      .select(getSelectedSupplierId)
      .pipe(
        map((entityId) => entityId?.trim()),
        filter((entityId): entityId is string => !!entityId),
        distinctUntilChanged()
      );
  }

  loadTaxCodes(): void {
    this._updateState({
      taxCodes: DEFAULT_ZRA_TAX_CODES,
      loading: true,
      error: null,
    });
    
    // Cancel any ongoing load
    this._loadCancellation$.next();
    
    this._entityId$
      .pipe(
        take(1),
        switchMap((entityId) => this.taxCodesService.getTaxCodes(entityId)),
        takeUntil(this._loadCancellation$)
      )
      .subscribe({
        next: (taxCodes) => {
          this._updateState({
            taxCodes,
            loading: false,
          });
        },
        error: (error) => {
          this._updateState({ error: error?.message, loading: false });
        },
      });
  }

  getTaxCodeByZraCode(zraCode: string): Observable<ZraTaxCode | undefined> {
    return this.taxCodes$.pipe(
      map(taxCodes => taxCodes.find(taxCode => taxCode.zraCode === zraCode))
    );
  }

  private _updateState(state: Partial<TaxCodesState>) {
    this._state$.next({ ...this._state$.value, ...state });
  }
} 
